import React, { useState, useEffect, useMemo } from 'react';
import { Package, Zap, Target, Users, Video, QrCode, Star, Gift, RefreshCw, Shield, BarChart3, UserPlus, TrendingUp } from 'lucide-react';
import { Machine } from '../../types/map';

interface CatalogItem {
  id: string;
  name: string;
  description: string;
  setupPrice: number;
  recurringPrice: number;
  unit: string;
  category: 'placement' | 'awareness' | 'engagement';
  icon: React.ReactNode;
  image?: string;
  unitType: string;
  chartData?: {
    metrics: Array<{
      label: string;
      value: number | string;
      unit: string;
      color: string;
      icon: React.ReactNode;
    }>;
    timeframe: string;
  };
}

type BlankableNumber = number | '';

interface RetailMediaSelectionStepProps {
  selectedMachines: Machine[];
  retailMediaSelections: Record<string, any>;
  onRetailMediaSelectionsChange: (selections: Record<string, any>) => void;
  isAlreadyListed?: boolean;
  skuCount?: number;
}

const catalogItems: CatalogItem[] = [
  {
    id: 'dooh-ad',
    name: 'DOOH Ad | 1 Slot/60s',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/2.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Brand Awareness Lift', value: 22, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> },
        { label: 'Total Impressions', value: '~37,500', unit: '/month/machine', color: 'bg-pink-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'home-banner',
    name: 'Home Banner Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/3.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Brand Awareness Uplift', value: 12, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-icon-lead',
    name: 'Category Icon Lead',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'awareness',
    icon: <Zap className="w-6 h-6" />,
    image: '/calculator-images/4.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 12, unit: '%', color: 'bg-orange-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-banner',
    name: 'Category Banner Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/5.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share Increase', value: 8, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'premium-listing',
    name: 'Premium Product Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/6.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 18, unit: '%', color: 'bg-blue-500', icon: <Package className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'exclusive-category',
    name: 'Exclusive Category',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Category Per Machine',
    category: 'placement',
    icon: <Shield className="w-6 h-6" />,
    image: '/calculator-images/7.png',
    unitType: 'Categories',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Market Share', value: 6, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 15, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'enhanced-detail',
    name: 'Enhanced Product Detail',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'engagement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/8.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 1.5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Conversion Rate', value: 10, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'vend-video',
    name: 'Vend Video Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Video className="w-6 h-6" />,
    image: '/calculator-images/9.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Captive Audience', value: 100, unit: '%', color: 'bg-green-500', icon: <Video className="w-4 h-4" /> },
        { label: 'Brand Awareness Lift', value: 6, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'virtual-sku',
    name: 'Virtual SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Package className="w-6 h-6" />,
    image: '/calculator-images/10.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Conversion Rate', value: '~8', unit: '%', color: 'bg-green-500', icon: <Package className="w-4 h-4" /> },
        { label: 'Retention Rate', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'bundle-sku',
    name: 'Bundle SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Gift className="w-6 h-6" />,
    image: '/calculator-images/11.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Opt In Rate', value: 8, unit: '%', color: 'bg-green-500', icon: <Package className="w-4 h-4" /> },
        { label: 'Intent to Purchase', value: 80, unit: '%', color: 'bg-blue-500', icon: <Gift className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'subscription-sku',
    name: 'Subscription SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <RefreshCw className="w-6 h-6" />,
    image: '/calculator-images/12.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Subscription Conversion', value: 4, unit: '%', color: 'bg-green-500', icon: <RefreshCw className="w-4 h-4" /> },
        { label: 'Customer Retention', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'safe-qr',
    name: 'SafeQR Redirect',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per QR Per Machine',
    category: 'engagement',
    icon: <QrCode className="w-6 h-6" />,
    image: '/calculator-images/13.png',
    unitType: 'QR Codes',
    chartData: {
      metrics: [],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-survey',
    name: 'Customer Survey',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Survey Per Machine',
    category: 'engagement',
    icon: <BarChart3 className="w-6 h-6" />,
    image: '/calculator-images/14.png',
    unitType: 'Surveys',
    chartData: {
      metrics: [
        { label: 'Survey Responses', value: 850, unit: '+', color: 'bg-green-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Completion Rate', value: 52, unit: '%', color: 'bg-blue-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-signup',
    name: 'Customer Sign-Up',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'engagement',
    icon: <UserPlus className="w-6 h-6" />,
    image: '/calculator-images/15.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Opt-In Rate', value: 20, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> },
        { label: 'Email Marketing ROI', value: 38, unit: '%', color: 'bg-purple-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  }
];

export function RetailMediaSelectionStep({
  selectedMachines,
  retailMediaSelections,
  onRetailMediaSelectionsChange,
  isAlreadyListed = false,
  skuCount = 0,
}: RetailMediaSelectionStepProps) {
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: { machines: BlankableNumber; units: BlankableNumber } }>(
    retailMediaSelections || {}
  );
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<4 | 8 | 12>(4);
  const [totalSetupPrice, setTotalSetupPrice] = useState(0);
  const [totalRecurringPrice, setTotalRecurringPrice] = useState(0);
  const [selectedProductDetail, setSelectedProductDetail] = useState<string | null>(null);
  const [itemPrices, setItemPrices] = useState<Record<string, { setupPriceCents: number; recurringPriceCents: number }>>({});
  const [loadingPrices, setLoadingPrices] = useState(true);

  const totalMachines = selectedMachines.length;

  // Extract unique venue types from selected machines
  const selectedVenueTypes = useMemo(() => {
    const venueTypesSet = new Set<string>();
    selectedMachines.forEach(machine => {
      if (machine.venueType) {
        venueTypesSet.add(machine.venueType);
      }
    });
    return Array.from(venueTypesSet);
  }, [selectedMachines]);

  // Fetch prices with multipliers applied
  useEffect(() => {
    const fetchPrices = async () => {
      if (selectedVenueTypes.length === 0) {
        setLoadingPrices(false);
        setItemPrices({});
        return;
      }

      try {
        setLoadingPrices(true);
        const res = await fetch('/api/store/retail-media-pricing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ venueTypes: selectedVenueTypes }),
        });

        if (res.ok) {
          const prices = await res.json();
          const priceMap: Record<string, { setupPriceCents: number; recurringPriceCents: number }> = {};
          prices.forEach((price: any) => {
            priceMap[price.itemId] = {
              setupPriceCents: price.setupPriceCents,
              recurringPriceCents: price.recurringPriceCents,
            };
          });
          setItemPrices(priceMap);
        } else {
          console.error('Failed to fetch retail media pricing');
        }
      } catch (error) {
        console.error('Error fetching retail media pricing:', error);
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchPrices();
  }, [selectedVenueTypes]);

  // Initialize pre-selections when machines are selected (or skip if already listed)
  useEffect(() => {
    if (Object.keys(selectedItems).length === 0) {
      if (totalMachines > 0) {
        // Use SKU count from previous step, default to 1 if no SKUs selected
        const skuUnits = skuCount > 0 ? skuCount : 1;
        // Pre-populate with selected machines count
        const initialSelections: { [key: string]: { machines: BlankableNumber; units: BlankableNumber } } = {
          'dooh-ad': { machines: totalMachines, units: 1 },
          'home-banner': { machines: totalMachines, units: 1 },
          'category-icon-lead': { machines: totalMachines, units: 1 },
          'category-banner': { machines: totalMachines, units: 1 },
          'premium-listing': { machines: totalMachines, units: skuUnits },
          'enhanced-detail': { machines: totalMachines, units: skuUnits },
        };
        setSelectedItems(initialSelections);
      } else if (isAlreadyListed) {
        // If already listed, start with empty selections so they can manually enter machine counts
        // Don't pre-populate anything
      }
    } else if (skuCount > 0 && totalMachines > 0) {
      // Update premium-listing and enhanced-detail units when SKU count changes
      setSelectedItems(prev => ({
        ...prev,
        'premium-listing': { 
          ...prev['premium-listing'], 
          machines: prev['premium-listing']?.machines || totalMachines,
          units: skuCount 
        },
        'enhanced-detail': { 
          ...prev['enhanced-detail'], 
          machines: prev['enhanced-detail']?.machines || totalMachines,
          units: skuCount 
        },
      }));
    }
  }, [totalMachines, isAlreadyListed, skuCount]);

  // Sync selectedItems to parent component
  useEffect(() => {
    onRetailMediaSelectionsChange(selectedItems);
  }, [selectedItems, onRetailMediaSelectionsChange]);

  // Calculate pricing using API-fetched prices
  useEffect(() => {
    let setupTotal = 0;
    let recurringTotal = 0;

    Object.entries(selectedItems).forEach(([itemId, config]) => {
      if (config.machines === '' || config.units === '') return;
      const machines = typeof config.machines === 'number' ? config.machines : 0;
      const units = typeof config.units === 'number' ? config.units : 0;

      if (machines > 0 && units > 0) {
        const price = itemPrices[itemId];
        if (!price) return;

        // Setup price is one-time per item type (not multiplied by machines/units)
        setupTotal += price.setupPriceCents;
        
        // Recurring price is per machine per unit, multiplied by time period
        const timePeriodMultiplier = selectedTimePeriod / 4;
        recurringTotal += price.recurringPriceCents * machines * units * timePeriodMultiplier;
      }
    });

    setTotalSetupPrice(setupTotal / 100); // Convert cents to pounds
    setTotalRecurringPrice(recurringTotal / 100); // Convert cents to pounds
  }, [selectedItems, selectedTimePeriod, itemPrices]);

  const handleMachineChange = (itemId: string, machines: BlankableNumber) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        machines: machines === '' ? '' : machines,
        units: prev[itemId]?.units || 0,
      },
    }));
  };

  const handleUnitChange = (itemId: string, units: BlankableNumber) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        machines: prev[itemId]?.machines || 0,
        units: units === '' ? '' : units,
      },
    }));
  };

  const getTotalQuantity = (itemId: string) => {
    const config = selectedItems[itemId];
    if (!config) return 0;
    const machines = config.machines === '' ? 0 : config.machines;
    const units = config.units === '' ? 0 : config.units;
    return (typeof machines === 'number' ? machines : 0) * (typeof units === 'number' ? units : 0);
  };

  const isSelected = (itemId: string) => getTotalQuantity(itemId) > 0;

  const getCurrentPricing = (item: CatalogItem) => {
    const price = itemPrices[item.id];
    if (!price) {
      return { setupPrice: 0, recurringPrice: 0 };
    }
    const timePeriodMultiplier = selectedTimePeriod / 4;
    return {
      setupPrice: price.setupPriceCents / 100, // Convert cents to pounds
      recurringPrice: (price.recurringPriceCents * timePeriodMultiplier) / 100, // Convert cents to pounds and apply time period
    };
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Choose Media</h2>
        <p className="text-slate-600">
          {isAlreadyListed 
            ? "Select retail media listings. Enter the number of machines for each item."
            : `Select retail media listings for your ${totalMachines} selected machine${totalMachines !== 1 ? 's' : ''}.`
          }
        </p>
      </div>

      {/* Pricing Summary Header */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 px-4 py-3">
        <div className="flex gap-2 mb-2">
          {[4, 8, 12].map((weeks) => (
            <button
              key={weeks}
              onClick={() => setSelectedTimePeriod(weeks as 4 | 8 | 12)}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
                selectedTimePeriod === weeks
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50'
              }`}
            >
              {weeks} Weeks
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="font-bold text-blue-900">Setup:</span>
          <span className="font-bold text-blue-900">£{totalSetupPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="text-xs text-blue-700 text-right -mt-1 mb-2">(one time per setup)</div>
        <div className="flex items-center justify-between text-sm">
          <span className="font-bold text-blue-900 mr-2">{selectedTimePeriod} Week Cost:</span>
          <span className="font-bold text-blue-900">£{totalRecurringPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="flex items-center justify-between text-sm pt-2 border-t border-blue-300 mt-1">
          <span className="font-bold text-blue-900">Total:</span>
          <span className="font-bold text-blue-900">£{(totalSetupPrice + totalRecurringPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      </div>

      {/* Items Grid */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {catalogItems.map((item) => (
            <div
              key={item.id}
              className={`rounded-xl shadow-sm border transition-all duration-300 overflow-hidden cursor-pointer flex ${
                isSelected(item.id)
                  ? 'bg-blue-200 border-blue-400 shadow-lg text-gray-900'
                  : 'bg-white border-gray-200 hover:shadow-lg hover:border-blue-300'
              }`}
              onClick={() => {
                if (!isSelected(item.id)) {
                  setSelectedItems((prev) => ({
                    ...prev,
                    [item.id]: { machines: totalMachines || 1, units: 1 },
                  }));
                } else {
                  setSelectedItems((prev) => {
                    const updated = { ...prev };
                    delete updated[item.id];
                    return updated;
                  });
                }
              }}
            >
              {/* Image Container */}
              {item.image && (
                <div
                  className="w-32 bg-white overflow-hidden cursor-pointer flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProductDetail(item.id);
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Content Container */}
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-2 rounded-lg ${
                        item.category === 'placement'
                          ? 'bg-green-100 text-green-600'
                          : item.category === 'awareness'
                          ? 'bg-blue-100 text-blue-600'
                          : item.category === 'engagement'
                          ? 'bg-purple-100 text-purple-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProductDetail(item.id);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                  >
                    View Listing
                  </button>
                </div>

                <div className="flex items-end justify-between">
                  <div className="text-sm">
                    <div className="font-bold text-gray-900">
                      Setup: £{getCurrentPricing(item).setupPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="text-xs text-gray-500 ml-1">(one time per setup)</span>
                    </div>
                    <div className="font-bold text-gray-900">
                      {selectedTimePeriod} Weeks: £{getCurrentPricing(item).recurringPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className={`text-xs mt-1 ${isSelected(item.id) ? 'text-gray-600' : 'text-gray-500'}`}>{item.unit}</div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    {/* Machines Control */}
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs w-16 ${isSelected(item.id) ? 'text-gray-600' : 'text-gray-500'}`}>Machines:</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMachineChange(item.id, (selectedItems[item.id]?.machines || 0) - 1);
                        }}
                        className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                        disabled={!selectedItems[item.id]?.machines || selectedItems[item.id]?.machines === ''}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="0"
                        {...(isAlreadyListed ? {} : { max: totalMachines })}
                        value={selectedItems[item.id]?.machines === 0 || selectedItems[item.id]?.machines === '' ? '' : selectedItems[item.id]?.machines || ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                          const finalValue = value === 0 ? '' : value;
                          handleMachineChange(item.id, finalValue);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="0"
                        className="w-12 h-6 text-center text-sm font-medium border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const currentMachines = typeof selectedItems[item.id]?.machines === 'number' ? selectedItems[item.id].machines : 0;
                          const newValue = typeof currentMachines === 'number' ? (isAlreadyListed ? currentMachines + 1 : Math.min(currentMachines + 1, totalMachines)) : 1;
                          handleMachineChange(item.id, newValue);
                        }}
                        className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                        disabled={!isAlreadyListed && typeof selectedItems[item.id]?.machines === 'number' && selectedItems[item.id].machines >= totalMachines}
                      >
                        +
                      </button>
                    </div>

                    {/* Units Control */}
                    <div className="flex items-center space-x-2">
                      <span className="text-xs w-16 text-gray-500">{item.unitType}:</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnitChange(item.id, (selectedItems[item.id]?.units || 0) - 1);
                        }}
                        className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                        disabled={!selectedItems[item.id]?.units || selectedItems[item.id]?.units === ''}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="0"
                        value={selectedItems[item.id]?.units === 0 || selectedItems[item.id]?.units === '' ? '' : selectedItems[item.id]?.units || ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                          const finalValue = value === 0 ? '' : value;
                          handleUnitChange(item.id, finalValue);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="0"
                        className="w-12 h-6 text-center text-sm font-medium border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnitChange(item.id, (selectedItems[item.id]?.units || 0) + 1);
                        }}
                        className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Detail Dialog */}
      {selectedProductDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedProductDetail(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const item = catalogItems.find(i => i.id === selectedProductDetail);
              if (!item) return null;
              
              return (
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`p-3 rounded-lg ${
                          item.category === 'placement'
                            ? 'bg-green-100 text-green-600'
                            : item.category === 'awareness'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-purple-100 text-purple-600'
                        }`}
                      >
                        {item.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.unit}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedProductDetail(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>
                  
                  {item.image && (
                    <div className="mb-4">
                      <img src={item.image} alt={item.name} className="w-full rounded-lg" />
                    </div>
                  )}
                  
                  {item.chartData && item.chartData.metrics.length > 0 && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {item.chartData.metrics.map((metric, idx) => (
                        <div key={idx} className={`${metric.color} p-4 rounded-lg text-white`}>
                          <div className="flex items-center gap-2 mb-2">
                            {metric.icon}
                            <span className="text-sm font-medium">{metric.label}</span>
                          </div>
                          <div className="text-2xl font-bold">{metric.value}{metric.unit}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-600">
                    <p><strong>Setup:</strong> £{getCurrentPricing(item).setupPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="text-xs text-gray-500 ml-1">(one time per setup)</span>
                    </p>
                    <p><strong>{selectedTimePeriod} Weeks:</strong> £{getCurrentPricing(item).recurringPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

